import React, { useState, useRef, useEffect } from 'react';
import { Upload, Brain, Zap, Settings, Eye, CheckCircle, User, Globe, Mic, Camera, Play, Volume2, StopCircle, X, Shield, AlertTriangle, Plug } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
// Note: Voice transcription/synthesis will be wired to backend APIs in a future iteration
import { useReplica } from '../contexts/ReplicaContext';
import { useAuth } from '../contexts/AuthContext';
import {
  CreateReplicaPanelProps,
  ReplicaFormData,
  VoiceSample,
  TrainingMaterial,
  Gender,
  AgeRange,
  Ethnicity,
  VoiceTone,
  DeploymentOption,
  GuardianAssignment,
  BoundaryConfig,
  GuardianConfig,
  ConsentConfirmation,
  SkillGapCategory
} from '../types';
import { replicaService } from '../lib/services';
import IntegrationsOnboardingStep from './IntegrationsOnboardingStep';

const CreateReplicaPanel: React.FC<CreateReplicaPanelProps> = ({
  isDashboardMode = false,
  onReplicaActionComplete,
  replicaId
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isRecording, setIsRecording] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [currentReplicaId, setCurrentReplicaId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isPreviewingVoice, setIsPreviewingVoice] = useState(false);
  const [selectedMcpTools, setSelectedMcpTools] = useState<string[]>([]);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Voice transcription & synthesis — will be wired to backend APIs in a future iteration
  const isLoading = false;
  const transcribeAudio = async (_file: File): Promise<string> => '';
  const generateSpeech = async (_text: string, _voice?: string): Promise<string> => '';

  const { deployReplica } = useReplica();
  const { user } = useAuth();

  const [formData, setFormData] = useState<ReplicaFormData>({
    // Basic Info
    name: '',
    category: '',
    description: '',

    // Avatar Creation
    avatarStyle: 'realistic',
    gender: 'neutral' as Gender,
    ageRange: 'adult' as AgeRange,
    ethnicity: 'diverse' as Ethnicity,
    avatarReferencePhoto: undefined,

    // Voice Training
    voiceTone: 'professional' as VoiceTone,
    voiceSpeed: 1.0,
    voiceSamples: [],

    // Expertise & Skills
    expertise: '',
    language: 'English',
    responseSpeed: 'fast',

    // Personality
    selectedPersonalityTraits: [],
    personalityDescription: '',

    // Training Materials
    trainingMaterials: [],
    sampleConversations: '',
    dosAndDonts: '',

    // Trust Hardening: Boundaries & Limits
    boundaries: {
      noExternalComms: true,
      noFinancialData: false,
      noPersonnelDecisions: true,
      noTransactions: true,
      noLegalInquiries: true,
      customBoundaries: [],
    } as BoundaryConfig,

    // Trust Hardening: Guardian Configuration
    guardianConfig: {
      guardianType: 'self' as GuardianAssignment,
      guardianEmail: '',
      financialThreshold: 500,
      externalCommsPolicy: 'always-approve',
      customEscalationRules: [],
    } as GuardianConfig,

    // Trust Hardening: Consent Confirmation
    consent: {
      understandsLimitations: false,
      acceptsResponsibility: false,
      confirmedGuardianSetup: false,
    } as ConsentConfirmation,

    // Deployment
    deploymentOption: 'website-widget' as DeploymentOption,
    guardianAssignment: 'self' as GuardianAssignment,

    // Evolution Framework: Skill Gap Profile
    skillGapProfile: {
      declaredGaps: [],
      idealComplement: '',
      workingStyle: '',
    },
  });

  // Load existing replica data from backend if replicaId is provided
  useEffect(() => {
    if (replicaId) {
      setCurrentReplicaId(replicaId);
      replicaService.get(replicaId).then(response => {
        if (response.data?.replica) {
          const r = response.data.replica;
          setFormData(prev => ({
            ...prev,
            name: r.name || '',
            category: r.category || '',
            description: r.description || '',
            avatarStyle: r.avatarStyle || 'realistic',
            gender: (r.avatarGender || 'neutral') as Gender,
            ageRange: (r.avatarAge || 'adult') as AgeRange,
            ethnicity: (r.avatarEthnicity || 'diverse') as Ethnicity,
            voiceTone: (r.voiceTone || 'professional') as VoiceTone,
            voiceSpeed: r.voiceSpeed ?? 1.0,
            expertise: r.expertise || '',
            language: r.language || 'English',
            responseSpeed: r.responseSpeed || 'fast',
            selectedPersonalityTraits: r.personalityTraits
              ? (typeof r.personalityTraits === 'string'
                ? (r.personalityTraits as string).split(', ').filter(Boolean)
                : Array.isArray(r.personalityTraits) ? r.personalityTraits : [])
              : [],
            personalityDescription: r.personality || '',
            deploymentOption: (r.deploymentOption || 'website-widget') as DeploymentOption,
            guardianAssignment: (r.guardianAssignment || 'auto-assign') as GuardianAssignment,
          }));
        }
      }).catch(err => {
        console.error('Failed to load replica from backend:', err);
      });
    }
  }, [replicaId]);

  const steps = [
    { id: 1, title: 'Purpose', icon: User },
    { id: 2, title: 'Avatar', icon: Camera },
    { id: 3, title: 'Voice', icon: Mic },
    { id: 4, title: 'Expertise', icon: Brain },
    { id: 5, title: 'Training', icon: Upload },
    { id: 6, title: 'Tools', icon: Plug },
    { id: 7, title: 'Governance', icon: Shield },
    { id: 8, title: 'Preview', icon: Eye },
    { id: 9, title: 'Confirm', icon: AlertTriangle },
    { id: 10, title: 'Activate', icon: Zap },
  ];

  const categories = [
    'Customer Service',
    'Sales',
    'HR',
    'Marketing',
    'Finance',
    'Technical Support',
    'Legal',
    'Healthcare',
    'Education',
    'Research',
    'Real Estate',
    'Logistics',
    'Insurance',
    'Consulting',
    'Engineering',
    'Retail',
    'Media & Entertainment',
    'Government',
    'Agriculture',
    'Hospitality',
  ];

  const personalityTraits = [
    'Professional & Formal',
    'Friendly & Casual',
    'Empathetic & Caring',
    'Direct & Efficient',
    'Creative & Innovative',
    'Analytical & Logical',
  ];

  const avatarStyles = [
    { id: 'realistic', name: 'Realistic', description: 'Photorealistic human appearance' },
    { id: 'stylized', name: 'Stylized', description: 'Artistic, illustrated style' },
    { id: 'minimal', name: 'Minimal', description: 'Simple, clean design' },
    { id: 'abstract', name: 'Abstract', description: 'Geometric, modern look' },
  ];

  const voicePrompts = [
    "Hello, I'm your AI assistant. How can I help you today?",
    "Thank you for contacting us. I'll be happy to assist you with your inquiry.",
  ];

  const handleInputChange = (field: keyof ReplicaFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleInputChange('avatarReferencePhoto', file);
    }
  };

  const handleTrainingFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newMaterials: TrainingMaterial[] = Array.from(files).map(file => ({
        id: Date.now().toString() + Math.random(),
        name: file.name,
        file,
        type: 'document',
      }));

      handleInputChange('trainingMaterials', [...formData.trainingMaterials, ...newMaterials]);
    }
  };

  const startRecording = async (promptIndex: number) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const options = { mimeType: 'audio/webm' };
      const recorder = new MediaRecorder(stream, options);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setAudioChunks(prev => [...prev, event.data]);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });

        try {
          const transcription = await transcribeAudio(audioFile);

          const newSample: VoiceSample = {
            id: Date.now().toString(),
            promptIndex,
            audioBlob,
            transcription,
            isRecorded: true,
          };

          const updatedSamples = [...formData.voiceSamples];
          const existingIndex = updatedSamples.findIndex(s => s.promptIndex === promptIndex);

          if (existingIndex >= 0) {
            updatedSamples[existingIndex] = newSample;
          } else {
            updatedSamples.push(newSample);
          }

          handleInputChange('voiceSamples', updatedSamples);
        } catch (err) {
          console.error('Transcription failed:', err);
          const newSample: VoiceSample = {
            id: Date.now().toString(),
            promptIndex,
            audioBlob,
            isRecorded: true,
          };

          const updatedSamples = [...formData.voiceSamples];
          const existingIndex = updatedSamples.findIndex(s => s.promptIndex === promptIndex);

          if (existingIndex >= 0) {
            updatedSamples[existingIndex] = newSample;
          } else {
            updatedSamples.push(newSample);
          }

          handleInputChange('voiceSamples', updatedSamples);
        }

        setAudioChunks([]);
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const playAudio = (audioBlob: Blob) => {
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    audio.play().catch(err => console.error('Audio playback failed:', err));
  };

  const handlePersonalityTraitToggle = (trait: string) => {
    const currentTraits = formData.selectedPersonalityTraits;
    const updatedTraits = currentTraits.includes(trait)
      ? currentTraits.filter(t => t !== trait)
      : [...currentTraits, trait];

    handleInputChange('selectedPersonalityTraits', updatedTraits);
  };

  const handleVoicePreview = async () => {
    setIsPreviewingVoice(true);
    try {
      const previewText = formData.name
        ? `Hello! I'm ${formData.name}, your AI replica. How can I assist you today?`
        : `Hello! I'm your AI replica. How can I assist you today?`;

      // Map voice tone to OpenAI voice names
      const voiceMap: Record<string, string> = {
        'professional': 'onyx',
        'friendly': 'nova',
        'warm': 'shimmer',
        'authoritative': 'echo',
      };
      const voice = voiceMap[formData.voiceTone] || 'alloy';

      // generateSpeech returns an audio URL string
      const audioUrl = await generateSpeech(previewText, voice);

      if (audioUrl) {
        const audio = new Audio(audioUrl);
        audio.play().catch(err => console.error('Audio playback failed:', err));
      }
    } catch (err) {
      console.error('Voice preview failed:', err);
    } finally {
      setIsPreviewingVoice(false);
    }
  };

  // ── Shared helpers ─────────────────────────────────────────────────────
  const buildReplicaPayload = () => ({
    name: formData.name,
    category: formData.category,
    description: formData.description,
    ownerId: user!.uid,
    avatarStyle: formData.avatarStyle,
    avatarGender: formData.gender,
    avatarAge: formData.ageRange,
    avatarEthnicity: formData.ethnicity,
    voiceTone: formData.voiceTone,
    voiceSpeed: typeof formData.voiceSpeed === 'number' ? formData.voiceSpeed : 1.0,
    expertise: formData.expertise,
    language: formData.language,
    responseSpeed: formData.responseSpeed,
    personalityTraits: formData.selectedPersonalityTraits,
    personalityDescription: formData.personalityDescription,
    // Governance
    boundaries: JSON.stringify(formData.boundaries),
    guardianConfigData: JSON.stringify(formData.guardianConfig),
    consentData: JSON.stringify(formData.consent),
    // Training text
    sampleConversations: formData.sampleConversations || null,
    dosAndDonts: formData.dosAndDonts || null,
    // Deployment
    deploymentOption: formData.deploymentOption,
    guardianAssignment: formData.guardianAssignment,
  });

  /** Create or update the replica in the backend and return the definitive ID. */
  const ensureReplicaCreated = async (): Promise<string> => {
    const payload = buildReplicaPayload();

    if (currentReplicaId) {
      // Update existing replica via API
      const response = await replicaService.update(currentReplicaId, payload);
      if (response.error) {
        throw new Error(response.error);
      }
      return currentReplicaId;
    }

    // Create new replica via backend
    const response = await replicaService.create(payload);
    if (response.error) {
      throw new Error(response.error);
    }
    if (!response.data?.replica?.id) {
      throw new Error('No replica ID returned from backend');
    }
    const newId = response.data.replica.id;
    setCurrentReplicaId(newId);
    return newId;
  };

  /** Upload voice samples, avatar, and training materials for the given replica. */
  const uploadAssets = async (replicaIdForUpload: string) => {
    // Upload voice samples
    if (formData.voiceSamples.length > 0) {
      for (const sample of formData.voiceSamples) {
        if (sample.audioBlob) {
          try {
            await replicaService.uploadVoiceSample(
              replicaIdForUpload,
              sample.audioBlob,
              sample.promptIndex,
              sample.transcription
            );
          } catch (uploadError) {
            console.warn('Voice sample upload failed:', uploadError);
          }
        }
      }
    }

    // Upload avatar photo
    if (formData.avatarReferencePhoto) {
      try {
        await replicaService.uploadMediaAsset(
          replicaIdForUpload,
          formData.avatarReferencePhoto,
          'AVATAR_PHOTO'
        );
      } catch (uploadError) {
        console.warn('Avatar upload failed:', uploadError);
      }
    }

    // Upload training materials
    if (formData.trainingMaterials.length > 0) {
      for (const material of formData.trainingMaterials) {
        try {
          await replicaService.uploadMediaAsset(
            replicaIdForUpload,
            material.file,
            'TRAINING_DOCUMENT'
          );
        } catch (uploadError) {
          console.warn('Training material upload failed:', uploadError);
        }
      }
    }

    // Setup MCP tool connectors
    if (selectedMcpTools.length > 0) {
      for (const toolId of selectedMcpTools) {
        try {
          await replicaService.setupMcpConnector(replicaIdForUpload, toolId);
        } catch (mcpError) {
          console.warn(`MCP connector setup failed for ${toolId}:`, mcpError);
        }
      }
    }
  };

  // ── Save (Complete) ────────────────────────────────────────────────────
  const handleCompleteReplica = async () => {
    if (!user?.uid) {
      setSaveError('Please log in to save your replica');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const replicaIdResult = await ensureReplicaCreated();
      await uploadAssets(replicaIdResult);

      if (isDashboardMode && onReplicaActionComplete) {
        onReplicaActionComplete();
      }
    } catch (error) {
      console.error('Failed to save replica:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save replica');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Deploy ──────────────────────────────────────────────────────────────
  const handleDeployReplica = async () => {
    if (!user?.uid) {
      setSaveError('Please log in to deploy your replica');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Ensure replica exists in backend first
      const replicaIdToDeploy = await ensureReplicaCreated();
      await uploadAssets(replicaIdToDeploy);

      // Deploy via backend API
      const deployResponse = await replicaService.deploy(replicaIdToDeploy);
      if (deployResponse.error) {
        throw new Error(deployResponse.error);
      }

      // Update local context for UI display
      deployReplica(replicaIdToDeploy);

      if (isDashboardMode && onReplicaActionComplete) {
        onReplicaActionComplete();
      }
    } catch (error) {
      console.error('Failed to deploy replica:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to deploy replica');
    } finally {
      setIsSaving(false);
    }
  };

  // Certificate data is generated lazily — only when the user clicks "Show Certificate"
  const generateCertificate = () => ({
    replicaId: currentReplicaId || `REP-${Date.now()}`,
    replicaName: formData.name || 'Unnamed Replica',
    creationDate: new Date().toISOString(),
    creator: user?.displayName || user?.email || 'User',
    category: formData.category,
    fingerprint: `0x${Math.random().toString(16).substr(2, 32)}`,
    qrData: `https://humanreplica.ai/verify/${currentReplicaId || Date.now()}`,
    blockchainTx: `0x${Math.random().toString(16).substr(2, 64)}`,
  });

  const isStepComplete = (stepId: number): boolean => {
    switch (stepId) {
      case 1:
        return !!(formData.name && formData.category && formData.description);
      case 2:
        return !!formData.avatarStyle;
      case 3:
        return true; // Voice recording is optional until voice pipeline is live
      case 4:
        // Merged Expertise + Personality
        return !!(formData.expertise && formData.language) &&
          (formData.selectedPersonalityTraits.length > 0 || !!formData.personalityDescription);
      case 5:
        return true; // Training is optional
      case 6:
        return true; // Tools is optional (can configure later)
      case 7:
        // Merged Boundaries + Guardian
        return (formData.boundaries.noExternalComms ||
          formData.boundaries.noFinancialData ||
          formData.boundaries.noPersonnelDecisions ||
          formData.boundaries.noTransactions ||
          formData.boundaries.noLegalInquiries ||
          formData.boundaries.customBoundaries.length > 0) &&
          !!formData.guardianConfig.guardianType &&
          formData.guardianConfig.financialThreshold > 0;
      case 8:
        return true; // Preview
      case 9:
        // Consent: All three checkboxes must be checked
        return formData.consent.understandsLimitations &&
          formData.consent.acceptsResponsibility &&
          formData.consent.confirmedGuardianSetup;
      case 10:
        return true; // Activate
      default:
        return false;
    }
  };

  const handleNextStep = () => {
    if (!isStepComplete(currentStep)) {
      // In a real app, show validation error toast here
      return;
    }

    // Fix 2: Sync step-7 guardian config into the legacy guardianAssignment field
    if (currentStep === 7) {
      setFormData(prev => ({
        ...prev,
        guardianAssignment: prev.guardianConfig.guardianType,
      }));
    }

    if (currentStep === 10) {
      handleCompleteReplica();
    } else {
      setCurrentStep(Math.min(10, currentStep + 1));
    }
  };

  // ── Step contextual metadata ──
  const stepMeta: Record<number, { title: string; subtitle: string; icon: React.ElementType }> = {
    1: {
      title: 'Purpose & Identity',
      subtitle: 'Every Replica starts with a clear mission. Define who it is and what it will do — this grounds everything that follows.',
      icon: User,
    },
    2: {
      title: 'Visual Presence',
      subtitle: 'The avatar is the first thing people see. It builds trust and makes interactions feel more human and approachable.',
      icon: Camera,
    },
    3: {
      title: 'Voice & Tone',
      subtitle: 'A voice gives your Replica a soul. Whether warm or professional, it shapes how people feel when interacting with it.',
      icon: Mic,
    },
    4: {
      title: 'Expertise & Personality',
      subtitle: 'Define what your Replica knows and how it communicates. Skills make it useful — personality makes it memorable.',
      icon: Brain,
    },
    5: {
      title: 'Knowledge Base',
      subtitle: 'Feed your Replica the documents and examples it needs. The more context it has, the more accurately it represents you.',
      icon: Upload,
    },
    6: {
      title: 'Tools & Integrations',
      subtitle: 'Connect your Replica to the tools it will use. Integrations turn it from a conversationalist into a productive agent.',
      icon: Plug,
    },
    7: {
      title: 'Safety & Governance',
      subtitle: 'Define what your Replica must never do and who reviews high-stakes decisions. This is what makes it trustworthy.',
      icon: Shield,
    },
    8: {
      title: 'Preview',
      subtitle: 'See your Replica as the world will see it. Review the full configuration before going live.',
      icon: Eye,
    },
    9: {
      title: 'Responsibility Confirmation',
      subtitle: 'With great power comes responsibility. Confirm that you understand and accept accountability for your Replica.',
      icon: AlertTriangle,
    },
    10: {
      title: 'Activate & Deploy',
      subtitle: 'The final step — choose how your Replica enters the world and bring it to life.',
      icon: Zap,
    },
  };

  // ── Step header component ──
  const StepHeader = ({ stepId }: { stepId: number }) => {
    const meta = stepMeta[stepId];
    if (!meta) return null;
    const Icon = meta.icon;
    return (
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-hig-accent/10 flex items-center justify-center text-hig-accent">
            <Icon size={20} />
          </div>
          <div>
            <p className="text-[11px] font-semibold text-hig-accent uppercase tracking-widest">
              Step {stepId} of 10
            </p>
            <h3 className="text-2xl font-semibold text-hig-text-primary">{meta.title}</h3>
          </div>
        </div>
        <p className="text-sm text-hig-text-secondary leading-relaxed ml-[52px] max-w-xl">
          {meta.subtitle}
        </p>
      </div>
    );
  };

  // HIG Input Style
  const inputClass = "w-full bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent focus:border-hig-accent transition-all";
  const labelClass = "block text-xs font-medium text-hig-text-secondary mb-1.5 uppercase tracking-wide";

  return (
    <div className="max-w-5xl mx-auto">
      {/* Smart Progress Bar */}
      <div className="mb-10 px-4">
        <div className="flex justify-between relative">
          {/* Connecting Line */}
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-hig-bg-tertiary -translate-y-1/2 z-0"></div>

          {steps.map((step) => {
            const isActive = currentStep === step.id;
            const isCompleted = step.id < currentStep;

            return (
              <div
                key={step.id}
                className="relative z-10 flex flex-col items-center cursor-pointer group"
                onClick={() => isCompleted && setCurrentStep(step.id)}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isActive
                    ? 'bg-hig-accent border-hig-accent text-white shadow-lg shadow-blue-500/20'
                    : isCompleted
                      ? 'bg-hig-bg-secondary border-hig-accent text-hig-accent'
                      : 'bg-hig-bg-secondary border-hig-border text-hig-text-tertiary'
                    }`}
                >
                  {isCompleted ? <CheckCircle size={18} /> : <step.icon size={18} />}
                </div>
                <span className={`absolute -bottom-6 text-[10px] font-medium tracking-wide uppercase whitespace-nowrap transition-colors ${isActive ? 'text-hig-text-primary' : 'text-hig-text-tertiary'
                  }`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Form Content */}
      <div className="glass p-8 min-h-[500px] flex flex-col">
        {/* Step 1: Basic Info */}
        {currentStep === 1 && (
          <div className="animate-fade-in">
            <StepHeader stepId={1} />
            <div className="space-y-6 max-w-2xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Replica Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., ALEX-AI"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Category *</label>
                  <div className="relative">
                    <select
                      value={showCustomCategory ? '__custom__' : formData.category}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setShowCustomCategory(true);
                          handleInputChange('category', '');
                        } else {
                          setShowCustomCategory(false);
                          handleInputChange('category', e.target.value);
                        }
                      }}
                      className={`${inputClass} appearance-none`}
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                      <option value="__custom__">✏️ Custom...</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-hig-text-tertiary">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                  </div>
                  {showCustomCategory && (
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      placeholder="Type your custom category..."
                      className={`${inputClass} mt-2`}
                      autoFocus
                    />
                  )}
                </div>
              </div>
              <div>
                <label className={labelClass}>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what your AI replica will do..."
                  className={`${inputClass} h-32 resize-none`}
                ></textarea>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Avatar */}
        {currentStep === 2 && (
          <div className="animate-fade-in">
            <StepHeader stepId={2} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Styles */}
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <label className={labelClass}>Visual Style</label>
                  <div className="grid grid-cols-2 gap-4">
                    {avatarStyles.map((style) => (
                      <div
                        key={style.id}
                        className={`cursor-pointer p-4 rounded-xl border transition-all ${formData.avatarStyle === style.id
                          ? 'bg-hig-bg-elevated border-hig-accent shadow-md'
                          : 'bg-hig-bg-tertiary border-transparent hover:bg-hig-bg-elevated'
                          }`}
                        onClick={() => handleInputChange('avatarStyle', style.id)}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formData.avatarStyle === style.id ? 'bg-hig-accent text-white' : 'bg-hig-bg-secondary text-hig-text-secondary'}`}>
                            <Camera size={14} />
                          </div>
                          <span className="font-medium text-hig-text-primary text-sm">{style.name}</span>
                        </div>
                        <p className="text-xs text-hig-text-secondary">{style.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Gender</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value as Gender)}
                      className={inputClass}
                    >
                      <option value="neutral">Neutral</option>
                      <option value="masculine">Masculine</option>
                      <option value="feminine">Feminine</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Age</label>
                    <select
                      value={formData.ageRange}
                      onChange={(e) => handleInputChange('ageRange', e.target.value as AgeRange)}
                      className={inputClass}
                    >
                      <option value="young-adult">Young Adult</option>
                      <option value="adult">Adult</option>
                      <option value="mature">Mature</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Ethnicity</label>
                    <select
                      value={formData.ethnicity}
                      onChange={(e) => handleInputChange('ethnicity', e.target.value as Ethnicity)}
                      className={inputClass}
                    >
                      <option value="diverse">Diverse</option>
                      <option value="caucasian">Caucasian</option>
                      <option value="asian">Asian</option>
                      <option value="african">African</option>
                      <option value="hispanic">Hispanic</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Upload */}
              <div className="lg:col-span-1">
                <label className={labelClass}>Reference Photo</label>
                <div className="aspect-square bg-hig-bg-tertiary border-2 border-dashed border-hig-border rounded-xl flex flex-col items-center justify-center text-center p-6 hover:border-hig-accent/50 transition-colors cursor-pointer group" onClick={() => fileInputRef.current?.click()}>
                  <div className="w-12 h-12 rounded-full bg-hig-bg-elevated flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Upload size={20} className="text-hig-text-secondary group-hover:text-hig-accent" />
                  </div>
                  <p className="text-sm font-medium text-hig-text-primary mb-1">Upload Photo</p>
                  <p className="text-xs text-hig-text-tertiary">JPG or PNG (Optional)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  {formData.avatarReferencePhoto && (
                    <div className="mt-4 px-3 py-1 bg-hig-success/10 text-hig-success rounded-full text-xs font-medium truncate max-w-full">
                      {formData.avatarReferencePhoto.name}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Voice */}
        {currentStep === 3 && (
          <div className="animate-fade-in">
            <StepHeader stepId={3} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <label className={labelClass}>Record at least 1 sample</label>
                {voicePrompts.map((prompt, index) => {
                  const sample = formData.voiceSamples.find(s => s.promptIndex === index);
                  return (
                    <div key={index} className="bg-hig-bg-tertiary rounded-lg p-4 transition-all">
                      <p className="text-sm text-hig-text-primary mb-3 italic">"{prompt}"</p>
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => isRecording ? stopRecording() : startRecording(index)}
                          disabled={isLoading}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isRecording ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20' : 'bg-hig-bg-elevated text-hig-text-primary hover:bg-hig-bg-elevated/80'
                            }`}
                        >
                          {isRecording ? <StopCircle size={14} /> : <Mic size={14} />}
                          {isRecording ? 'Stop' : 'Record'}
                        </button>
                        {sample?.isRecorded && (
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-hig-success font-medium flex items-center gap-1">
                              <CheckCircle size={12} />
                              Done
                            </span>
                            {sample.audioBlob && (
                              <button onClick={() => playAudio(sample.audioBlob!)} className="text-hig-accent hover:text-hig-accent/80">
                                <Play size={14} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="space-y-6">
                <div className="bg-hig-bg-elevated rounded-xl p-6 border border-hig-border/50">
                  <h4 className="font-medium text-hig-text-primary mb-4 flex items-center gap-2">
                    <Settings size={16} />
                    Voice Settings
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Tone</label>
                      <select
                        value={formData.voiceTone}
                        onChange={(e) => handleInputChange('voiceTone', e.target.value as VoiceTone)}
                        className={inputClass}
                      >
                        <option value="professional">Professional</option>
                        <option value="friendly">Friendly</option>
                        <option value="warm">Warm</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Speed ({formData.voiceSpeed}x)</label>
                      <input
                        type="range"
                        className="w-full h-1 bg-hig-bg-tertiary rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-hig-accent"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={formData.voiceSpeed}
                        onChange={(e) => handleInputChange('voiceSpeed', parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-hig-bg-accent/5 rounded-lg border border-hig-accent/10">
                  <Volume2 size={20} className="text-hig-accent mt-1" />
                  <div>
                    <h5 className="text-sm font-medium text-hig-text-primary">Preview Voice</h5>
                    <p className="text-xs text-hig-text-secondary mt-1 mb-2">Listen to how your replica sounds with current settings.</p>
                    <button
                      onClick={handleVoicePreview}
                      disabled={isPreviewingVoice || isLoading}
                      className="text-xs font-semibold text-hig-accent hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPreviewingVoice ? 'Generating...' : 'Play Demo'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Expertise & Personality (Merged) */}
        {currentStep === 4 && (
          <div className="animate-fade-in max-w-3xl">
            <StepHeader stepId={4} />
            <div className="space-y-8">
              {/* Expertise section */}
              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Primary Expertise</label>
                  <textarea
                    value={formData.expertise}
                    onChange={(e) => handleInputChange('expertise', e.target.value)}
                    placeholder="Detail the specific skills and knowledge areas..."
                    className={`${inputClass} h-32 resize-none`}
                  ></textarea>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Primary Language</label>
                    <select
                      value={formData.language}
                      onChange={(e) => handleInputChange('language', e.target.value)}
                      className={inputClass}
                    >
                      <option value="English">English</option>
                      <option value="French">French</option>
                      <option value="Spanish">Spanish</option>
                      <option value="German">German</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Response Speed</label>
                    <select
                      value={formData.responseSpeed}
                      onChange={(e) => handleInputChange('responseSpeed', e.target.value)}
                      className={inputClass}
                    >
                      <option value="instant">Instant (&lt; 1s)</option>
                      <option value="fast">Fast (1-3s)</option>
                      <option value="thoughtful">Thoughtful (3-5s)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-hig-divider" />

              {/* ═══ EVOLUTION: Skill Gap Questionnaire ═══ */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-hig-text-primary mb-1 flex items-center gap-2">
                    <span>🧬</span> Where Do You Need Help?
                  </h4>
                  <p className="text-xs text-hig-text-tertiary mb-4">
                    Your Replica will focus on becoming strongest in the areas you're weakest. This powers the <span className="text-hig-accent font-medium">Learn → Replicate → Apex</span> evolution.
                  </p>
                  <label className={labelClass}>Select Your Gaps</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {([
                      { id: 'financial-analysis' as SkillGapCategory, icon: '💰', label: 'Financial Analysis' },
                      { id: 'public-writing' as SkillGapCategory, icon: '✍️', label: 'Public Writing' },
                      { id: 'time-management' as SkillGapCategory, icon: '⏰', label: 'Time Management' },
                      { id: 'data-interpretation' as SkillGapCategory, icon: '📊', label: 'Data Interpretation' },
                      { id: 'legal-review' as SkillGapCategory, icon: '⚖️', label: 'Legal Review' },
                      { id: 'cold-outreach' as SkillGapCategory, icon: '📧', label: 'Cold Outreach' },
                      { id: 'detail-orientation' as SkillGapCategory, icon: '🔍', label: 'Detail Orientation' },
                      { id: 'creative-design' as SkillGapCategory, icon: '🎨', label: 'Creative Design' },
                      { id: 'technical-coding' as SkillGapCategory, icon: '💻', label: 'Technical / Coding' },
                      { id: 'project-management' as SkillGapCategory, icon: '📋', label: 'Project Management' },
                      { id: 'negotiation' as SkillGapCategory, icon: '🤝', label: 'Negotiation' },
                      { id: 'research' as SkillGapCategory, icon: '🔬', label: 'Research' },
                    ]).map((gap) => {
                      const selected = formData.skillGapProfile?.declaredGaps?.includes(gap.id) ?? false;
                      return (
                        <div
                          key={gap.id}
                          className={`flex items-center gap-2.5 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                            selected
                              ? 'bg-sky-500/10 border-sky-500/40 text-sky-400 font-medium ring-1 ring-sky-500/20'
                              : 'bg-hig-bg-tertiary border-transparent text-hig-text-secondary hover:bg-hig-bg-elevated'
                          }`}
                          onClick={() => {
                            const prev = formData.skillGapProfile?.declaredGaps ?? [];
                            const updated = selected ? prev.filter(g => g !== gap.id) : [...prev, gap.id];
                            handleInputChange('skillGapProfile', {
                              ...formData.skillGapProfile,
                              declaredGaps: updated,
                            });
                          }}
                        >
                          <span className="text-base">{gap.icon}</span>
                          <span className="text-sm">{gap.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Describe Your Ideal Complement</label>
                  <textarea
                    value={formData.skillGapProfile?.idealComplement ?? ''}
                    onChange={(e) => handleInputChange('skillGapProfile', {
                      ...formData.skillGapProfile,
                      idealComplement: e.target.value,
                    })}
                    placeholder="What would your perfect work partner be strong at? E.g. 'Someone who catches every detail I miss and turns rough ideas into polished deliverables...'"
                    className={`${inputClass} h-24 resize-none`}
                  ></textarea>
                </div>
                <div>
                  <label className={labelClass}>Your Working Style</label>
                  <textarea
                    value={formData.skillGapProfile?.workingStyle ?? ''}
                    onChange={(e) => handleInputChange('skillGapProfile', {
                      ...formData.skillGapProfile,
                      workingStyle: e.target.value,
                    })}
                    placeholder="How do you work? E.g. 'I move fast, decide intuitively, and prefer big-picture thinking over details...'"
                    className={`${inputClass} h-24 resize-none`}
                  ></textarea>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-hig-divider" />

              {/* Personality section */}
              <div className="space-y-6">
                <h4 className="text-base font-semibold text-hig-text-primary">Communication Style</h4>
                <div>
                  <label className={labelClass}>Traits</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {personalityTraits.map((trait) => (
                      <div
                        key={trait}
                        className={`px-4 py-3 rounded-lg border cursor-pointer transition-all ${formData.selectedPersonalityTraits.includes(trait)
                          ? 'bg-hig-accent/10 border-hig-accent text-hig-accent font-medium'
                          : 'bg-hig-bg-tertiary border-transparent text-hig-text-secondary hover:bg-hig-bg-elevated'
                          }`}
                        onClick={() => handlePersonalityTraitToggle(trait)}
                      >
                        {trait}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Communication Style Guide</label>
                  <textarea
                    value={formData.personalityDescription}
                    onChange={(e) => handleInputChange('personalityDescription', e.target.value)}
                    placeholder="Describe how the replica should speak, its tone, and behavioral nuances..."
                    className={`${inputClass} h-32 resize-none`}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Training Materials */}
        {currentStep === 5 && (
          <div className="animate-fade-in">
            <StepHeader stepId={5} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Upload */}
              <div className="bg-hig-bg-tertiary border-2 border-dashed border-hig-border rounded-xl p-8 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-hig-bg-elevated rounded-full flex items-center justify-center mb-4">
                  <Upload size={24} className="text-hig-text-secondary" />
                </div>
                <h4 className="font-medium text-hig-text-primary mb-2">Upload Training Data</h4>
                <p className="text-xs text-hig-text-secondary mb-6 max-w-xs">Supported: PDF, DOCX, TXT. <br /> Max 10MB.</p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleTrainingFileUpload}
                  className="hidden"
                  id="training-files"
                />
                <label
                  htmlFor="training-files"
                  className="btn-secondary cursor-pointer"
                >
                  Select Files
                </label>
                {formData.trainingMaterials.length > 0 && (
                  <div className="mt-8 w-full">
                    <p className="text-xs font-semibold text-hig-text-secondary uppercase mb-3 text-left">Uploaded</p>
                    <div className="space-y-2">
                      {formData.trainingMaterials.map((material) => (
                        <div key={material.id} className="flex items-center gap-2 text-sm text-hig-text-primary bg-hig-bg-elevated px-3 py-2 rounded">
                          <CheckCircle size={14} className="text-hig-success" />
                          <span className="truncate">{material.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Example Convos */}
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Sample Conversations</label>
                  <textarea
                    value={formData.sampleConversations}
                    onChange={(e) => handleInputChange('sampleConversations', e.target.value)}
                    placeholder="Paste example dialogues..."
                    className={`${inputClass} h-32 resize-none`}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Tools & Integrations (MCP) */}
        {currentStep === 6 && (
          <IntegrationsOnboardingStep
            replicaCategory={formData.category}
            selectedTools={selectedMcpTools}
            onToolsChange={setSelectedMcpTools}
          />
        )}

        {/* Step 7: Safety & Governance (Merged Boundaries + Guardian) */}
        {currentStep === 7 && (
          <div className="animate-fade-in">
            <StepHeader stepId={7} />

            <div className="space-y-8">
              {/* Warning Banner */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-hig-text-primary mb-1">This Step Is Required</h4>
                  <p className="text-sm text-hig-text-secondary">
                    Defining boundaries and a guardian is not optional. This is what makes your Replica trustworthy.
                  </p>
                </div>
              </div>

              {/* ── Boundaries Section ── */}
              <div className="bg-hig-bg-tertiary rounded-xl p-6">
                <h4 className="font-semibold text-hig-text-primary mb-4">This Replica will NOT:</h4>
                <div className="space-y-3">
                  {[
                    { key: 'noExternalComms', label: 'Send external communications without human approval', description: 'Emails, messages, or any outbound communication' },
                    { key: 'noFinancialData', label: 'Access or process financial data', description: 'Bank accounts, payment info, transactions' },
                    { key: 'noPersonnelDecisions', label: 'Make hiring or personnel recommendations', description: 'HR decisions, performance reviews, terminations' },
                    { key: 'noTransactions', label: 'Execute financial transactions', description: 'Payments, refunds, purchases, transfers' },
                    { key: 'noLegalInquiries', label: 'Respond to legal inquiries', description: 'Legal advice, compliance questions, contracts' },
                  ].map((boundary) => (
                    <label
                      key={boundary.key}
                      className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all ${formData.boundaries[boundary.key as keyof typeof formData.boundaries]
                        ? 'bg-red-500/10 border border-red-500/30'
                        : 'bg-hig-bg-elevated border border-transparent hover:border-hig-border'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.boundaries[boundary.key as keyof typeof formData.boundaries] as boolean}
                        onChange={(e) => handleInputChange('boundaries', {
                          ...formData.boundaries,
                          [boundary.key]: e.target.checked
                        })}
                        className="mt-1 w-4 h-4 rounded border-hig-border text-red-500 focus:ring-red-500"
                      />
                      <div>
                        <span className="font-medium text-hig-text-primary block">{boundary.label}</span>
                        <span className="text-xs text-hig-text-tertiary">{boundary.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Custom Boundaries */}
              <div className="bg-hig-bg-tertiary rounded-xl p-6">
                <h4 className="font-semibold text-hig-text-primary mb-3">Custom Boundaries</h4>
                <p className="text-sm text-hig-text-secondary mb-4">Add any additional restrictions specific to your use case.</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g., Never discuss competitor products"
                    className={inputClass}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                        handleInputChange('boundaries', {
                          ...formData.boundaries,
                          customBoundaries: [...formData.boundaries.customBoundaries, (e.target as HTMLInputElement).value.trim()]
                        });
                        (e.target as HTMLInputElement).value = '';
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.querySelector('input[placeholder*="Never discuss"]') as HTMLInputElement;
                      if (input?.value.trim()) {
                        handleInputChange('boundaries', {
                          ...formData.boundaries,
                          customBoundaries: [...formData.boundaries.customBoundaries, input.value.trim()]
                        });
                        input.value = '';
                      }
                    }}
                    className="btn-secondary whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
                {formData.boundaries.customBoundaries.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {formData.boundaries.customBoundaries.map((boundary, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/10 text-red-400 rounded-full text-sm"
                      >
                        {boundary}
                        <button
                          onClick={() => handleInputChange('boundaries', {
                            ...formData.boundaries,
                            customBoundaries: formData.boundaries.customBoundaries.filter((_, i) => i !== index)
                          })}
                          className="ml-1 hover:text-red-300"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Divider ── */}
              <div className="border-t border-hig-divider" />

              {/* ── Guardian Section ── */}
              <div className="bg-hig-accent/10 border border-hig-accent/30 rounded-xl p-4 flex items-start gap-3">
                <Shield size={20} className="text-hig-accent flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-hig-text-primary mb-1">Decision Guardian™</h4>
                  <p className="text-sm text-hig-text-secondary">
                    High-stakes decisions will pause and wait for your approval. Nothing critical executes without human consent.
                  </p>
                </div>
              </div>

              <div className="bg-hig-bg-tertiary rounded-xl p-6">
                <h4 className="font-semibold text-hig-text-primary mb-4">Who will review escalated decisions?</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label
                    className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all ${formData.guardianConfig.guardianType === 'self'
                      ? 'bg-hig-accent/10 border border-hig-accent/50'
                      : 'bg-hig-bg-elevated border border-transparent hover:border-hig-border'
                      }`}
                  >
                    <input
                      type="radio"
                      name="guardianType"
                      value="self"
                      checked={formData.guardianConfig.guardianType === 'self'}
                      onChange={() => handleInputChange('guardianConfig', {
                        ...formData.guardianConfig,
                        guardianType: 'self' as GuardianAssignment
                      })}
                      className="mt-1"
                    />
                    <div>
                      <span className="font-medium text-hig-text-primary block">I will be the Guardian</span>
                      <span className="text-xs text-hig-text-tertiary">All escalated decisions come to me for approval</span>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all ${formData.guardianConfig.guardianType === 'invite-member'
                      ? 'bg-hig-accent/10 border border-hig-accent/50'
                      : 'bg-hig-bg-elevated border border-transparent hover:border-hig-border'
                      }`}
                  >
                    <input
                      type="radio"
                      name="guardianType"
                      value="invite-member"
                      checked={formData.guardianConfig.guardianType === 'invite-member'}
                      onChange={() => handleInputChange('guardianConfig', {
                        ...formData.guardianConfig,
                        guardianType: 'invite-member' as GuardianAssignment
                      })}
                      className="mt-1"
                    />
                    <div>
                      <span className="font-medium text-hig-text-primary block">Invite a Team Member</span>
                      <span className="text-xs text-hig-text-tertiary">Designate another person as the guardian</span>
                    </div>
                  </label>
                </div>

                {formData.guardianConfig.guardianType === 'invite-member' && (
                  <div className="mt-4">
                    <label className={labelClass}>Guardian Email</label>
                    <input
                      type="email"
                      value={formData.guardianConfig.guardianEmail || ''}
                      onChange={(e) => handleInputChange('guardianConfig', {
                        ...formData.guardianConfig,
                        guardianEmail: e.target.value
                      })}
                      placeholder="guardian@company.com"
                      className={inputClass}
                    />
                  </div>
                )}
              </div>

              {/* Escalation Thresholds */}
              <div className="bg-hig-bg-tertiary rounded-xl p-6">
                <h4 className="font-semibold text-hig-text-primary mb-4">When should this Replica pause for approval?</h4>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Financial decisions above</label>
                    <div className="flex items-center gap-2">
                      <span className="text-hig-text-secondary">$</span>
                      <input
                        type="number"
                        value={formData.guardianConfig.financialThreshold}
                        onChange={(e) => handleInputChange('guardianConfig', {
                          ...formData.guardianConfig,
                          financialThreshold: parseInt(e.target.value) || 0
                        })}
                        className={`${inputClass} w-32`}
                        min="0"
                        step="100"
                      />
                      <span className="text-sm text-hig-text-tertiary">requires human approval</span>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>External Communications</label>
                    <div className="flex gap-4">
                      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${formData.guardianConfig.externalCommsPolicy === 'always-approve'
                        ? 'bg-hig-accent/10 border border-hig-accent/50'
                        : 'bg-hig-bg-elevated border border-transparent'
                        }`}>
                        <input
                          type="radio"
                          name="externalComms"
                          value="always-approve"
                          checked={formData.guardianConfig.externalCommsPolicy === 'always-approve'}
                          onChange={() => handleInputChange('guardianConfig', {
                            ...formData.guardianConfig,
                            externalCommsPolicy: 'always-approve'
                          })}
                        />
                        <span className="text-sm text-hig-text-primary">Always require approval</span>
                      </label>
                      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${formData.guardianConfig.externalCommsPolicy === 'draft-only'
                        ? 'bg-hig-accent/10 border border-hig-accent/50'
                        : 'bg-hig-bg-elevated border border-transparent'
                        }`}>
                        <input
                          type="radio"
                          name="externalComms"
                          value="draft-only"
                          checked={formData.guardianConfig.externalCommsPolicy === 'draft-only'}
                          onChange={() => handleInputChange('guardianConfig', {
                            ...formData.guardianConfig,
                            externalCommsPolicy: 'draft-only'
                          })}
                        />
                        <span className="text-sm text-hig-text-primary">Draft only, never send</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-hig-bg-elevated rounded-xl p-4 border border-hig-border">
                <h4 className="font-medium text-hig-text-primary mb-2">Configuration Summary</h4>
                <ul className="text-sm text-hig-text-secondary space-y-1">
                  <li>• Guardian: <span className="text-hig-text-primary">{formData.guardianConfig.guardianType === 'self' ? 'You' : formData.guardianConfig.guardianEmail || 'Not set'}</span></li>
                  <li>• Financial threshold: <span className="text-hig-text-primary">${formData.guardianConfig.financialThreshold}</span></li>
                  <li>• External comms: <span className="text-hig-text-primary">{formData.guardianConfig.externalCommsPolicy === 'always-approve' ? 'Require approval' : 'Draft only'}</span></li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Step 8: Preview */}
        {currentStep === 8 && (
          <div className="animate-fade-in">
            <StepHeader stepId={8} />
            <div className="bg-hig-bg-tertiary rounded-xl overflow-hidden border border-hig-border">
              <div className="p-6 border-b border-hig-border bg-hig-bg-secondary">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                    <span className="text-2xl font-bold">{formData.name ? formData.name.charAt(0) : 'A'}</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-hig-text-primary">{formData.name || 'Replica Name'}</h4>
                    <p className="text-hig-text-secondary">{formData.category}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="bg-hig-bg-elevated rounded-lg p-4 space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-500 flex-shrink-0"></div>
                    <div className="bg-hig-bg-tertiary p-3 rounded-2xl rounded-tl-none text-sm text-hig-text-primary">
                      Hello, I need assistance with a complex issue.
                    </div>
                  </div>
                  <div className="flex gap-3 flex-row-reverse">
                    <div className="w-8 h-8 rounded-full bg-hig-accent flex-shrink-0"></div>
                    <div className="bg-hig-accent text-white p-3 rounded-2xl rounded-tr-none text-sm shadow-md">
                      {formData.name || 'Replica'}: Hello! I'm ready to help. Please tell me more about what you're facing.
                    </div>
                  </div>
                </div>

                {/* Configuration Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-hig-bg-elevated rounded-lg p-4">
                    <h5 className="font-medium text-hig-text-primary mb-2 flex items-center gap-2">
                      <AlertTriangle size={16} className="text-amber-500" />
                      Boundaries Set
                    </h5>
                    <ul className="text-sm text-hig-text-secondary space-y-1">
                      {formData.boundaries.noExternalComms && <li>• No external communications</li>}
                      {formData.boundaries.noFinancialData && <li>• No financial data access</li>}
                      {formData.boundaries.noPersonnelDecisions && <li>• No personnel decisions</li>}
                      {formData.boundaries.noTransactions && <li>• No transactions</li>}
                      {formData.boundaries.noLegalInquiries && <li>• No legal inquiries</li>}
                      {formData.boundaries.customBoundaries.map((b, i) => <li key={i}>• {b}</li>)}
                    </ul>
                  </div>
                  <div className="bg-hig-bg-elevated rounded-lg p-4">
                    <h5 className="font-medium text-hig-text-primary mb-2 flex items-center gap-2">
                      <Shield size={16} className="text-hig-accent" />
                      Guardian Configuration
                    </h5>
                    <ul className="text-sm text-hig-text-secondary space-y-1">
                      <li>• Guardian: {formData.guardianConfig.guardianType === 'self' ? 'You' : formData.guardianConfig.guardianEmail}</li>
                      <li>• Financial threshold: ${formData.guardianConfig.financialThreshold}</li>
                      <li>• External comms: {formData.guardianConfig.externalCommsPolicy === 'always-approve' ? 'Require approval' : 'Draft only'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 9: Responsibility Confirmation */}
        {currentStep === 9 && (
          <div className="animate-fade-in max-w-2xl mx-auto">
            <StepHeader stepId={9} />

            {/* Warning */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-hig-text-primary mb-1">This Is Required</h4>
                <p className="text-sm text-hig-text-secondary">
                  All three acknowledgments must be confirmed before you can activate this Replica.
                </p>
              </div>
            </div>

            {/* Consent Checkboxes */}
            <div className="space-y-4">
              <label
                className={`flex items-start gap-4 p-5 rounded-xl cursor-pointer transition-all ${formData.consent.understandsLimitations
                  ? 'bg-hig-success/10 border border-hig-success/30'
                  : 'bg-hig-bg-tertiary border border-transparent hover:border-hig-border'
                  }`}
              >
                <input
                  type="checkbox"
                  checked={formData.consent.understandsLimitations}
                  onChange={(e) => handleInputChange('consent', {
                    ...formData.consent,
                    understandsLimitations: e.target.checked
                  })}
                  className="mt-1 w-5 h-5 rounded border-hig-border text-hig-success focus:ring-hig-success"
                />
                <div>
                  <span className="font-medium text-hig-text-primary block mb-1">
                    I understand this Replica's limitations
                  </span>
                  <span className="text-sm text-hig-text-secondary">
                    This Replica will prepare recommendations and assist with tasks, but cannot make final decisions autonomously. All high-stakes actions require human approval.
                  </span>
                </div>
              </label>

              <label
                className={`flex items-start gap-4 p-5 rounded-xl cursor-pointer transition-all ${formData.consent.acceptsResponsibility
                  ? 'bg-hig-success/10 border border-hig-success/30'
                  : 'bg-hig-bg-tertiary border border-transparent hover:border-hig-border'
                  }`}
              >
                <input
                  type="checkbox"
                  checked={formData.consent.acceptsResponsibility}
                  onChange={(e) => handleInputChange('consent', {
                    ...formData.consent,
                    acceptsResponsibility: e.target.checked
                  })}
                  className="mt-1 w-5 h-5 rounded border-hig-border text-hig-success focus:ring-hig-success"
                />
                <div>
                  <span className="font-medium text-hig-text-primary block mb-1">
                    I remain responsible for this Replica's actions
                  </span>
                  <span className="text-sm text-hig-text-secondary">
                    As the creator, I accept accountability for how this Replica is used and the decisions made under my guidance. I will review and respond to escalated decisions promptly.
                  </span>
                </div>
              </label>

              <label
                className={`flex items-start gap-4 p-5 rounded-xl cursor-pointer transition-all ${formData.consent.confirmedGuardianSetup
                  ? 'bg-hig-success/10 border border-hig-success/30'
                  : 'bg-hig-bg-tertiary border border-transparent hover:border-hig-border'
                  }`}
              >
                <input
                  type="checkbox"
                  checked={formData.consent.confirmedGuardianSetup}
                  onChange={(e) => handleInputChange('consent', {
                    ...formData.consent,
                    confirmedGuardianSetup: e.target.checked
                  })}
                  className="mt-1 w-5 h-5 rounded border-hig-border text-hig-success focus:ring-hig-success"
                />
                <div>
                  <span className="font-medium text-hig-text-primary block mb-1">
                    I have configured Decision Guardian™ oversight
                  </span>
                  <span className="text-sm text-hig-text-secondary">
                    I have reviewed and configured the escalation rules. I understand that high-stakes decisions will pause for human approval and I or my designated guardian will respond.
                  </span>
                </div>
              </label>
            </div>

            {/* Progress indicator */}
            <div className="mt-6 text-center">
              <p className="text-sm text-hig-text-tertiary">
                {[formData.consent.understandsLimitations, formData.consent.acceptsResponsibility, formData.consent.confirmedGuardianSetup].filter(Boolean).length} of 3 confirmations complete
              </p>
            </div>
          </div>
        )}

        {/* Step 10: Activate & Deploy */}
        {currentStep === 10 && (
          <div className="animate-fade-in max-w-2xl mx-auto text-center">
            <StepHeader stepId={10} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 text-left">
              {[
                { value: 'website-widget', label: 'Web Widget', icon: Globe },
                { value: 'api-integration', label: 'API Access', icon: Zap },
                { value: 'mobile-app', label: 'Mobile App', icon: StopCircle },
                { value: 'voice-assistant', label: 'Voice Assistant', icon: Mic },
              ].map((option) => (
                <label key={option.value} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${formData.deploymentOption === option.value
                  ? 'bg-hig-text-primary text-hig-bg-primary border-transparent'
                  : 'bg-hig-bg-elevated border-transparent text-hig-text-secondary hover:border-hig-text-tertiary'
                  }`}>
                  <input
                    type="radio"
                    name="deployment"
                    value={option.value}
                    checked={formData.deploymentOption === option.value}
                    onChange={(e) => handleInputChange('deploymentOption', e.target.value as DeploymentOption)}
                    className="hidden"
                  />
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${formData.deploymentOption === option.value ? 'border-hig-bg-primary' : 'border-hig-text-tertiary'
                    }`}>
                    {formData.deploymentOption === option.value && <div className="w-2.5 h-2.5 rounded-full bg-hig-bg-primary"></div>}
                  </div>
                  <span className="font-medium flex-1">{option.label}</span>
                  <option.icon size={16} />
                </label>
              ))}
            </div>

            <div className="flex gap-4 justify-center flex-col items-center">
              {saveError && (
                <div className="text-red-500 text-sm mb-2 bg-red-500/10 px-4 py-2 rounded-lg">
                  {saveError}
                </div>
              )}
              <div className="flex gap-4">
                <button
                  onClick={handleCompleteReplica}
                  disabled={isSaving}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  onClick={handleDeployReplica}
                  disabled={isSaving}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Activating...' : 'Activate Replica'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Navigation */}
        <div className="mt-auto pt-8 border-t border-hig-divider flex justify-between items-center">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            className={`text-sm font-medium text-hig-text-secondary hover:text-hig-text-primary px-4 py-2 ${currentStep === 1 ? 'invisible' : ''}`}
          >
            Back
          </button>

          <div className="flex gap-4 items-center">
            {currentStep < 10 && isStepComplete(1) && (
              <button
                onClick={handleCompleteReplica}
                disabled={isSaving}
                className="text-sm font-medium text-hig-text-secondary hover:text-hig-text-primary px-4 py-2"
              >
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>
            )}

            {currentStep < 10 && (
              <button
                onClick={handleNextStep}
                className={`btn-primary ${!isStepComplete(currentStep) ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!isStepComplete(currentStep)}
              >
                Next Step
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Certificate Modal */}
      {showCertificate && (() => {
        const certificate = generateCertificate();
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="glass max-w-4xl w-full p-8 rounded-2xl relative shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowCertificate(false)}
                className="absolute top-4 right-4 text-hig-text-secondary hover:text-hig-text-primary"
              >
                <X size={24} />
              </button>

              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-hig-text-primary mb-2">AI Replica Birth Certificate</h2>
                <p className="text-hig-text-secondary">Blockchain-Verified Authenticity Document</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 text-left">
                <div>
                  <h3 className="text-lg font-semibold text-hig-text-primary mb-4">Replica Details</h3>
                  <div className="space-y-3">
                    <div className="bg-hig-bg-tertiary p-3 rounded-lg">
                      <span className="text-xs text-hig-text-secondary block mb-1">Replica Name</span>
                      <p className="font-medium text-hig-text-primary">{certificate.replicaName}</p>
                    </div>
                    <div className="bg-hig-bg-tertiary p-3 rounded-lg">
                      <span className="text-xs text-hig-text-secondary block mb-1">Creation Date</span>
                      <p className="font-medium text-hig-text-primary">
                        {new Date(certificate.creationDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="bg-hig-bg-tertiary p-3 rounded-lg">
                      <span className="text-xs text-hig-text-secondary block mb-1">Certificate ID</span>
                      <p className="font-medium text-hig-text-primary">{certificate.replicaId}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-hig-text-primary mb-4">Blockchain Verification</h3>
                  <div className="bg-hig-bg-tertiary p-4 rounded-lg h-full flex flex-col justify-center items-center text-center">
                    <QRCodeSVG value={certificate.qrData} size={120} className="mb-4" />
                    <p className="text-xs font-mono text-hig-text-secondary break-all">{certificate.blockchainTx}</p>
                    <div className="mt-2 text-[10px] text-hig-success font-bold uppercase tracking-widest border border-hig-success px-2 py-1 rounded">Verified on Chain</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default CreateReplicaPanel;